// hls.js is loaded dynamically inside tryUrl (below) so its ~500KB isn't
// bundled into every homepage/list-page load just for hover-preview trailers.
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import SmartImage from './SmartImage';
import { useWatchlist } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';

const CDN = 'https://d2q1oltu9dnqie.cloudfront.net/';

// Format duration: "136m" → "2h 08m", "47m" → "47m"
function formatDuration(raw) {
  if (!raw) return null;
  const str = String(raw).trim();
  if (/^\d+h/.test(str)) return str;
  const hms = str.match(/^(\d+):(\d{2}):\d{2}$/);
  if (hms) {
    const h = parseInt(hms[1], 10), m = parseInt(hms[2], 10);
    return h > 0 ? `${h}h ${m.toString().padStart(2,'0')}m` : `${m}m`;
  }
  const mins = str.match(/^(\d+)\s*m?$/i);
  if (mins) {
    const total = parseInt(mins[1], 10);
    const h = Math.floor(total / 60), m = total % 60;
    return h > 0 ? `${h}h ${m.toString().padStart(2,'0')}m` : `${m}m`;
  }
  return str;
}

// Normalize any CDN URL to the current active CloudFront distribution.
// The API server may return any of these older domains in trailerUrl/videoUrl:
//   - cdn.shortflixindia.com          (custom domain, may point to old dist)
//   - d12it2ctp7su6p.cloudfront.net   (old CloudFront dist — now inactive)
// Both must be rewritten to the current active dist: d2q1oltu9dnqie.cloudfront.net
function normalizeCdnUrl(url) {
  if (!url) return url;
  // Signed video URLs (/t/{token}/) must NEVER be rewritten — token breaks.
  if (url.includes('/t/')) return url;
  return url
    .replace('https://cdn.shortflixindia.com/', 'https://d2q1oltu9dnqie.cloudfront.net/')
    .replace('https://d12it2ctp7su6p.cloudfront.net/', 'https://d2q1oltu9dnqie.cloudfront.net/');
}

// Server source (util.js line 108):
// getTrailerUrl = (filmid, path='') => `{CDN}{filmid}-trailer{path}/playlist.m3u8`
// trailerUrl is ONLY set when show_trailer !== 0
// CDN folder is always filmid — never filmname/slug
// Trailers must resolve to cdn.shortflixindia.com — that domain has correct
// CORS for the production origin. The open CDN does not work for trailers.
const TRAILER_CDN = 'https://cdn.shortflixindia.com/';

function fixTrailerCdn(url) {
  if (!url || url.includes('/t/')) return url;
  return url
    .replace('https://d2q1oltu9dnqie.cloudfront.net/', TRAILER_CDN)
    .replace('https://d12it2ctp7su6p.cloudfront.net/', TRAILER_CDN);
}

function buildTrailerUrls(movie) {
  const urls = [];
  const api = movie?.trailerUrl || movie?.trailerurl;
  if (api) urls.push(fixTrailerCdn(api));
  if (!api && movie?.show_trailer === 0) {
    return [];
  }
  const fid = movie?.filmid || movie?.id || movie?.programid;
  if (fid) {
    const u = `${TRAILER_CDN}${fid}-trailer/playlist.m3u8`;
    urls.push(u);
  }
  return [...new Set(urls)];
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const PlayIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
);
const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="3"/>
  </svg>
);
const MuteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);
const UnmuteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
  </svg>
);

// ── HLS Trailer player ────────────────────────────────────────────────────────
// Props: urls (MUST be stable reference via useMemo), muteRef, videoElRef
// muteRef.current  = current mute state (ref, not state — never causes re-render)
// videoElRef.current = the <video> DOM node (set via useEffect so parent can write .muted)
//
// WHY THIS DESIGN:
// Mute toggle must NOT re-render this component. If it did, useEffect([urls]) would
// see a new urls array (even if content is identical) and restart HLS from scratch.
// Solution: parent stores mute in a ref + writes video.muted directly to DOM.
// This component only re-renders when urls changes (i.e. new movie hovered).
function PopupTrailer({ urls, muteRef, videoElRef, onFail }) {
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);

  // Give parent direct access to the video DOM node for mute writes
  useEffect(() => {
    if (videoElRef) videoElRef.current = videoRef.current;
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !urls?.length) return;
    let cancelled = false;
    let idx = 0;

    function destroy() {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) {}
        hlsRef.current = null;
      }
    }

    function tryNext() {
      if (cancelled) return;
      if (idx < urls.length) tryUrl(urls[idx++]);
      else onFail?.();
    }

    async function tryUrl(src) {
      destroy();
      // Safari native HLS
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
        video.muted = true;
        video.playsInline = true;
        video.play()
          .then(() => { if (!cancelled) video.muted = muteRef.current; })
          .catch(() => { if (!cancelled) tryNext(); });
        return;
      }
      // hls.js for Chrome/Firefox — loaded on demand, not bundled upfront
      if (cancelled) return;
      const { default: Hls } = await import('hls.js');
      if (cancelled || !Hls.isSupported()) { tryNext(); return; }
      const hls = new Hls({ startLevel: -1, maxBufferLength: 20, enableWorker: false,
        xhrSetup: xhr => { xhr.withCredentials = false; } });
      hlsRef.current = hls;
      hls.on(Hls.Events.ERROR, (_, d) => { if (d.fatal && !cancelled) tryNext(); });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        video.muted = true; video.playsInline = true;
        video.play()
          .then(() => { if (!cancelled) video.muted = muteRef.current; })
          .catch(() => { if (!cancelled) onFail?.(); });
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    }

    tryUrl(urls[idx++]);

    return () => {
      cancelled = true;
      destroy();
      if (video) { video.pause(); video.src = ''; }
    };
  }, [urls]); // ← stable ref from useMemo — never fires on mute toggle

  return <video ref={videoRef} className="nf-popup-trailer" muted playsInline autoPlay />;
}

// ── Animated watchlist button ─────────────────────────────────────────────
function WatchlistBtn({ isInList, filmId, movieTitle, onWatchlistToggle }) {
  const [animating, setAnimating] = React.useState(false);
  const [localIn,   setLocalIn]   = React.useState(isInList);

  // Sync if external source changes isInList (e.g. added from detail page)
  React.useEffect(() => { setLocalIn(isInList); }, [isInList]);

  // Try to get toast — silently skip if ToastProvider not in tree
  let toast = null;
  try { toast = require('./Toast').useToast(); } catch (_) {}

  function handle(e) {
    e.stopPropagation();
    const next = !localIn;
    setLocalIn(next);
    setAnimating(true);
    onWatchlistToggle?.(filmId, localIn); // pass localIn (pre-toggle state) not stale prop
    if (toast) {
      next
        ? toast.success(`Added to My List`, { duration: 2500 })
        : toast.info(`Removed from My List`, { duration: 2000 });
    }
    setTimeout(() => setAnimating(false), 500);
  }

  return (
    <button
      className={`nf-popup-btn nf-watchlist-btn${localIn ? ' nf-popup-btn--green' : ''}${animating ? ' wl-animating' : ''}`}
      title={localIn ? 'Remove from My List' : 'Add to My List'}
      onClick={handle}
    >
      {localIn
        ? <span className="wl-icon"><CheckIcon /></span>
        : <span className="wl-icon"><PlusIcon /></span>
      }
    </button>
  );
}

// ── HoverPopup ────────────────────────────────────────────────────────────────
function HoverPopup({ movie, anchorRect, closeTimerRef, onClose, onWatchlistToggle }) {
  const navigate  = useNavigate();
  const filmId    = movie.filmid || movie.id || movie.programid;
  const { list: _popupList } = useWatchlist();
  // Prefer live watchlist state over stale favitem field
  const isInList  = _popupList.some(m => (m.filmid || m.id) === filmId) || movie.favitem === 1;
  const isPremium = movie.premium === 1;
  const pct       = movie.watchedpercentage ?? 0;
  const { isAuthenticated, isPremium: hasSubscription, signIn } = useAuth();

  // Same subscription gate as HeroBanner's Watch Now / ShowDetailPage's
  // "Subscribe to Watch" — this popup's Play button was skipping it and
  // playing the full movie for anyone, subscribed or not. Gate on the
  // user's real subscription state (hasSubscription), not movie.premium —
  // the backend detail endpoint always hardcodes premium to 0, so it can't
  // be trusted to tell free content apart from paid content.
  const handlePlay = (e) => {
    e.stopPropagation();
    onClose();
    if (!isAuthenticated) {
      sessionStorage.setItem('sf_post_login_redirect', `/show/${filmId}`);
      signIn();
      return;
    }
    if (!hasSubscription) {
      sessionStorage.setItem('sf_post_subscribe_redirect', `/show/${filmId}`);
      navigate('/subscription');
      return;
    }
    navigate(`/watch/${filmId}/main`);
  };

  // ── Mute: ref only — changing mute MUST NOT re-render HoverPopup ──────────
  // If mute state lived in useState here, every toggle would re-render HoverPopup
  // → trailerUrls (even if memoized) would be passed as a new prop reference
  //   because React re-creates the JSX tree → PopupTrailer sees new urls → HLS restarts.
  // Fix: mute lives in ref. Button icon is a SEPARATE tiny component with its own state.
  const muteRef    = useRef(true);
  const videoElRef = useRef(null);

  // CRITICAL: useMemo with movie identity as key
  // This ensures trailerUrls is the SAME array reference across re-renders
  // unless the actual movie object changes (new card hovered)
  const trailerUrls = useMemo(
    () => buildTrailerUrls(movie),
    [movie?.filmid, movie?.trailerUrl, movie?.trailerurl, movie?.show_trailer] // eslint-disable-line
  );
  const hasTrailer = trailerUrls.length > 0;

  const [showTrailer,   setShowTrailer]   = useState(false);
  const [trailerFailed, setTrailerFailed] = useState(false);

  useEffect(() => {
    if (!hasTrailer) return;
    const t = setTimeout(() => setShowTrailer(true), 700);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  // ── Popup positioning ────────────────────────────────────────────────────
  // Popup is position:fixed — anchorRect coords are viewport-relative.
  // anchorRect was captured at mouseenter BEFORE CSS hover transform fired,
  // so coords represent the card's real resting position.
  const POPUP_W  = 300;
  const VW       = window.innerWidth;
  const VH       = window.innerHeight;
  const PAD      = 8;     // minimum gap from viewport edge
  const NAVBAR_H = 68;    // navbar height — never render above this

  // Card center in viewport coords
  const cardMidX = anchorRect.left + anchorRect.width / 2;
  const cardTopY = anchorRect.top;

  // Horizontal: center popup on card, clamp so it never bleeds off screen edges
  let left = Math.round(cardMidX - POPUP_W / 2);
  left = Math.max(PAD, Math.min(left, VW - POPUP_W - PAD));

  // Vertical: align popup top with card top, nudged up slightly so it
  // "grows over" the card. Clamp: never above navbar, never below viewport.
  // Estimate popup height for bottom-clamp (info section ~180px + image 190px)
  const POPUP_H_EST = 390;
  let top = Math.round(cardTopY - 8);
  top = Math.max(NAVBAR_H + 4, top);
  // If popup would go below viewport, flip it upward from card top
  if (top + POPUP_H_EST > VH - PAD) {
    top = Math.max(NAVBAR_H + 4, cardTopY - POPUP_H_EST + anchorRect.height);
  }

  // Transform-origin: point toward the card so the scale animation feels natural
  const actualMid = left + POPUP_W / 2;
  const diff      = actualMid - cardMidX;
  const origin    = diff < -20 ? 'top right' : diff > 20 ? 'top left' : 'top center';

  const genres = [movie.genres, movie.genre, movie.category]
    .filter(Boolean).join(',').split(',')
    .map(g => g.trim()).filter(g => g && g.toLowerCase() !== 'banner').slice(0, 3);
  const year  = movie.releaseyear || movie.year || (movie.releasedate || '').slice(0, 4);
  const dur   = formatDuration(movie.durationtext || movie.duration);
  const desc  = movie.description || movie.storyline || movie.plot || movie.synopsis || '';
  const short = desc.length > 130 ? desc.slice(0, 127) + '…' : desc;

  const showVideo = showTrailer && !trailerFailed && hasTrailer;

  function handlePopupEnter() { clearTimeout(closeTimerRef.current); }
  function handlePopupLeave() { closeTimerRef.current = setTimeout(onClose, 250); }

  return ReactDOM.createPortal(
    <div className="nf-popup" style={{ left, top, width: POPUP_W, transformOrigin: origin, cursor: 'pointer' }}
      onMouseEnter={handlePopupEnter} onMouseLeave={handlePopupLeave}>

      {/* Clicking the media/image area navigates to detail page */}
      <div className="nf-popup-media" onClick={() => { onClose(); navigate(`/show/${filmId}`); }}>
        <SmartImage movie={movie} type="landscape" alt={movie.title} className="nf-popup-img" />

        {showVideo && (
          <PopupTrailer
            urls={trailerUrls}
            muteRef={muteRef}
            videoElRef={videoElRef}
            onFail={() => setTrailerFailed(true)}
          />
        )}

        {isPremium && !showVideo && <div className="nf-popup-badge">Premium</div>}
        <div className="nf-popup-gradient" />
        <div className="nf-popup-title">{movie.title}</div>

        {pct > 0 && pct < 100 && (
          <div className="nf-popup-pbar">
            <div className="nf-popup-pbar-fill" style={{ width: `${Math.min(pct,100)}%` }} />
          </div>
        )}

        {/* MuteButton is its own component with its own state — changes here
            never cause HoverPopup or PopupTrailer to re-render */}
        {showVideo && (
          <MuteButton muteRef={muteRef} videoElRef={videoElRef} />
        )}
      </div>

      <div className="nf-popup-info">
        <div className="nf-popup-actions">
          <div className="nf-popup-left">
            <button className="nf-popup-play" title="Play" onClick={handlePlay}>
              <PlayIcon />
            </button>
            <WatchlistBtn
              isInList={isInList}
              filmId={filmId}
              onWatchlistToggle={onWatchlistToggle}
            />
          </div>
          <button className="nf-popup-btn" title="More Info"
            onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/show/${filmId}`); }}>
            <InfoIcon />
          </button>
        </div>

        <div className="nf-popup-meta">
          {movie.rating && <span className="nf-popup-rating">★ {movie.rating}</span>}
          {year         && <span className="nf-popup-chip">{year}</span>}
          {dur          && <span className="nf-popup-chip">{dur}</span>}
          {/4k|uhd|2160/i.test(movie.filmresultion || '') &&
            <span className="meta-badge meta-badge--4k nf-popup-quality">4K</span>}
          {/5\.1|dolby|surround|atmos/i.test(movie.audiolanguages || '') &&
            <span className="meta-badge meta-badge--audio nf-popup-quality">5.1</span>}
          {pct > 0 && pct < 100 && <span className="nf-popup-watched">{Math.round(pct)}% watched</span>}
        </div>

        {genres.length > 0 && (
          <div className="nf-popup-genres">
            {genres.map((g, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="nf-popup-dot">•</span>}
                <span>{g}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        {short && <p className="nf-popup-desc">{short}</p>}
      </div>
    </div>,
    document.body
  );
}

// ── MuteButton — isolated component so its state changes don't bubble up ──────
// This is the KEY architectural fix for the mute restart bug.
// When mute is toggled:
//   BEFORE: setMuted in HoverPopup → HoverPopup re-renders → new trailerUrls reference
//           → PopupTrailer re-renders → useEffect([urls]) fires → HLS RESTARTS
//   AFTER:  MuteButton has its own local state → only MuteButton re-renders
//           → HoverPopup does NOT re-render → trailerUrls stays same reference
//           → PopupTrailer does NOT re-render → HLS keeps playing ✓
function MuteButton({ muteRef, videoElRef }) {
  const [muted, setMuted] = useState(true);

  function toggle(e) {
    e.stopPropagation();
    const next = !muteRef.current;
    muteRef.current = next;
    // Direct DOM write — does not trigger any React re-render above this component
    if (videoElRef.current) videoElRef.current.muted = next;
    setMuted(next); // only re-renders THIS button
  }

  return (
    <button className="nf-popup-mute" onClick={toggle} title={muted ? 'Unmute' : 'Mute'}>
      {muted ? <MuteIcon /> : <UnmuteIcon />}
    </button>
  );
}

// ── Shared hover hook ─────────────────────────────────────────────────────────
function useHoverPopup(noPopup = false) {
  const cardRef       = useRef(null);
  const openTimerRef  = useRef(null);
  const closeTimerRef = useRef(null);
  // Store the rect captured at mouseenter (before CSS transform animates)
  const rawRectRef    = useRef(null);
  const [showPopup,  setShowPopup]  = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  useEffect(() => {
    const onScroll = () => {
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
      setShowPopup(false);
      setAnchorRect(null);
      rawRectRef.current = null;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  function handleEnter() {
    if (noPopup) return;
    clearTimeout(closeTimerRef.current);
    // Capture rect IMMEDIATELY on mouseenter, before CSS hover transform fires.
    // After 400ms the card has scaled/translated — using that rect misaligns the popup.
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Store a plain object copy — DOMRect is live and changes as transforms animate
    rawRectRef.current = {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      // Use the rect captured at mouseenter, not the post-animation rect
      setAnchorRect(rawRectRef.current);
      setShowPopup(true);
    }, 380);
  }

  function handleLeave() {
    clearTimeout(openTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setShowPopup(false);
      setAnchorRect(null);
      rawRectRef.current = null;
    }, 250);
  }

  const closePopup = useCallback(() => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    setShowPopup(false);
    setAnchorRect(null);
    rawRectRef.current = null;
  }, []);

  return { cardRef, showPopup, anchorRect, closeTimerRef, handleEnter, handleLeave, closePopup };
}

// ── MovieCard ─────────────────────────────────────────────────────────────────
export function MovieCard({ movie, onWatchlistToggle: _onWatchlistToggle, noPopup }) {
  const { list: _wlList, toggleWatchlist: _toggle } = useWatchlist();
  // Use prop if provided (parent manages state), otherwise fall back to internal hook
  const onWatchlistToggle = _onWatchlistToggle || _toggle;
  // Derive isInList from internal watchlist too (favitem may be stale after toggle)
  const _cardFilmId = movie?.filmid || movie?.id || movie?.programid;
  const _isInListInternal = _wlList.some(m => (m.filmid || m.id) === _cardFilmId);
  const navigate = useNavigate();
  const { cardRef, showPopup, anchorRect, closeTimerRef, handleEnter, handleLeave, closePopup } = useHoverPopup(noPopup);
  if (!movie) return null;
  const isInList = _isInListInternal || movie.favitem === 1;
  const pct      = movie.watchedpercentage;
  const filmId   = movie.filmid || movie.id || movie.programid;

  return (
    <>
      <div ref={cardRef}
        className={`movie-card${showPopup ? ' movie-card--active' : ''}`}
        onClick={() => navigate(`/show/${filmId}`)}
        onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        <div className="movie-thumb">
          <SmartImage movie={movie} type="portrait" alt={movie.title} className="movie-thumb-img" />
          {movie.premium === 1 && <div className="premium-badge">Premium</div>}
          <div className="movie-overlay">
            <div className="overlay-actions">
              <div className="overlay-play"><PlayIcon /></div>
              {onWatchlistToggle && (
                <button className={`overlay-watchlist-btn${isInList ? ' in-list' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onWatchlistToggle(filmId, isInList); }}>
                  {isInList ? <CheckIcon /> : <PlusIcon />}
                </button>
              )}
            </div>
          </div>
          {pct > 0 && pct < 100 && (
            <div className="progress-bar-bottom">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      </div>
      {showPopup && anchorRect && (
        <HoverPopup movie={movie} anchorRect={anchorRect} closeTimerRef={closeTimerRef}
          onClose={closePopup} onWatchlistToggle={onWatchlistToggle} />
      )}
    </>
  );
}

// ── WideCard ──────────────────────────────────────────────────────────────────
export function WideCard({ movie, onWatchlistToggle: _onWatchlistToggleW }) {
  const { list: _wlListW, toggleWatchlist: _toggleW } = useWatchlist();
  const onWatchlistToggle = _onWatchlistToggleW || _toggleW;
  const navigate = useNavigate();
  const { cardRef, showPopup, anchorRect, closeTimerRef, handleEnter, handleLeave, closePopup } = useHoverPopup(false);
  if (!movie) return null;
  const pct    = movie.watchedpercentage ?? 0;
  const filmId = movie.filmid || movie.id || movie.programid;

  return (
    <>
      <div ref={cardRef}
        className={`wide-card${showPopup ? ' wide-card--active' : ''}`}
        onClick={() => navigate(`/show/${filmId}`)}
        onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        <div className="wide-thumb">
          <SmartImage movie={movie} type="landscape" alt={movie.title} className="wide-thumb-img" />
          <div className="wide-overlay-play"><PlayIcon /></div>
          <div className="continue-bar">
            <div className="continue-progress" style={{ width: `${Math.min(pct,100)}%` }} />
          </div>
        </div>
      </div>
      {showPopup && anchorRect && (
        <HoverPopup movie={movie} anchorRect={anchorRect} closeTimerRef={closeTimerRef}
          onClose={closePopup} onWatchlistToggle={onWatchlistToggle} />
      )}
    </>
  );
}

// ── ContentRow ────────────────────────────────────────────────────────────────
export function ContentRow({ title, movies = [], orientation = 'V', onWatchlistToggle }) {
  const rowRef    = useRef(null);
  const [hovered,  setHovered]  = useState(false);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!movies.length) return null;
  // On mobile, always stack portrait cards for a clean, neat row —
  // landscape "wide" cards only apply on tablet/desktop.
  const isWide = orientation === 'L' && !isMobile;

  function updateArrows() {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }

  function scroll(dir) {
    const el = rowRef.current;
    if (!el) return;
    const cardW = isWide ? 298 : 180;
    const visible = Math.max(1, Math.floor(el.clientWidth / cardW));
    el.scrollBy({ left: dir * visible * cardW, behavior: 'smooth' });
    setTimeout(updateArrows, 500);
  }

  return (
    <div className="content-section"
      onMouseEnter={() => { setHovered(true); updateArrows(); }}
      onMouseLeave={() => setHovered(false)}>
      <div className="section-header"><h2 className="section-title">{title}</h2></div>
      <div className="content-section-wrap">
        <button className={`nf-arrow nf-arrow--left${hovered && canLeft ? ' nf-arrow--visible' : ''}`}
          onClick={() => scroll(-1)} aria-label="Scroll left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div className="content-row" ref={rowRef} onScroll={updateArrows}>
          {movies.map((m) => {
            const key = m.filmid || m.id || m.programid || Math.random();
            return isWide
              ? <WideCard  key={key} movie={m} onWatchlistToggle={onWatchlistToggle} />
              : <MovieCard key={key} movie={m} onWatchlistToggle={onWatchlistToggle} />;
          })}
        </div>
        <button className={`nf-arrow nf-arrow--right${hovered && canRight ? ' nf-arrow--visible' : ''}`}
          onClick={() => scroll(1)} aria-label="Scroll right">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
